"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import type { ImageResolution } from "@saas/ai-image-generator/types";
import { getResolutionLabel } from "@saas/ai-image-generator/types";
import { usePurchases } from "@saas/payments/hooks/purchases";
import { WatermarkedImage } from "@saas/shared/components/WatermarkedImage";
import { downloadWithWatermark } from "@shared/lib/watermark-canvas";
import { Button } from "@ui/components/button";
import { cn } from "@ui/lib";
import {
	DownloadIcon,
	EditIcon,
	ImageIcon,
	RefreshCwIcon,
	StarIcon,
	Trash2Icon,
	VideoIcon,
	XIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type ImageGeneration = {
	id: string;
	originalPrompt: string;
	model: string;
	imageUrls: string[];
	createdAt: string;
	metadata?: {
		parameters?: {
			aspect_ratio?: string;
			resolution?: string;
		};
		provider?: {
			parameters?: {
				resolution?: string;
			};
		};
	} | null;
	inputImageUrls?: string[];
};

interface ImageDetailModalProps {
	isOpen: boolean;
	onClose: () => void;
	generation: ImageGeneration;
	initialImageIndex?: number;
	onReprompt?: (generation: ImageGeneration) => void;
	onRegenerate?: (generation: ImageGeneration) => void;
	onGenerateVideo?: (imageUrl: string) => void;
	/** 收藏和删除都是单张图级别的，状态和写接口都在 AssetGrid 那边 */
	isImageFavorite?: (imageIndex: number) => boolean;
	onToggleFavorite?: (imageIndex: number) => void;
	onDelete?: (imageIndex: number) => void;
}

export function ImageDetailModal({
	isOpen,
	onClose,
	generation,
	initialImageIndex = 0,
	onReprompt,
	onRegenerate,
	onGenerateVideo,
	isImageFavorite,
	onToggleFavorite,
	onDelete,
}: ImageDetailModalProps) {
	const t = useTranslations();
	const {
		canRemoveWatermark,
		loading: purchasesLoading,
		isError: purchasesError,
	} = usePurchases();
	const [currentImageIndex, setCurrentImageIndex] =
		useState(initialImageIndex);
	/** 点大图铺满整屏看细节，左右切换和键盘导航照旧 */
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [isPromptExpanded, setIsPromptExpanded] = useState(false);

	// 重置图片索引当模态框打开时
	useEffect(() => {
		if (isOpen) {
			setCurrentImageIndex(initialImageIndex);
			setIsFullscreen(false);
			setIsPromptExpanded(false);
		}
	}, [isOpen, initialImageIndex]);

	// 键盘导航
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (!isOpen) return;

			if (e.key === "ArrowLeft" && currentImageIndex > 0) {
				setCurrentImageIndex(currentImageIndex - 1);
			} else if (
				e.key === "ArrowRight" &&
				currentImageIndex < generation.imageUrls.length - 1
			) {
				setCurrentImageIndex(currentImageIndex + 1);
			} else if (e.key === "Escape") {
				// 全屏时 Esc 先退全屏，别把整个弹窗一起关了
				if (isFullscreen) {
					setIsFullscreen(false);
				} else {
					onClose();
				}
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [
		isOpen,
		isFullscreen,
		currentImageIndex,
		generation.imageUrls.length,
		onClose,
	]);

	const currentImageUrl = generation.imageUrls[currentImageIndex];
	// 长 prompt 先收到 6 行，免得移动端整页被撑得太长
	const isPromptLong = generation.originalPrompt.length > 200;
	const aspectRatio = generation.metadata?.parameters?.aspect_ratio || "1:1";
	// 档位存在 parameters.resolution，部分 provider 只写在自己那层；v3.0 站内值和
	// 展示 label 对不上，所以统一过一遍 getResolutionLabel
	const storedResolution =
		generation.metadata?.parameters?.resolution ??
		generation.metadata?.provider?.parameters?.resolution;
	const resolution = storedResolution
		? getResolutionLabel(
				generation.model,
				storedResolution as ImageResolution,
			).label
		: null;

	const formatDateTime = (date: Date) => {
		const locale =
			typeof navigator !== "undefined" && navigator.language
				? navigator.language
				: "en-US";
		return new Intl.DateTimeFormat(locale, {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
		}).format(date);
	};

	const handleDownload = async (imageUrl: string) => {
		try {
			// 没有去水印权限的账号下载带水印版本；判断口径同 WatermarkedImage，
			// purchases 没拉到时不加，别让付了钱的人下到水印图
			if (!purchasesLoading && !purchasesError && !canRemoveWatermark) {
				await downloadWithWatermark(imageUrl);
				return;
			}
			const proxyUrl = `/api/proxy-download?url=${encodeURIComponent(imageUrl)}`;
			const a = document.createElement("a");
			a.href = proxyUrl;
			document.body.appendChild(a);
			a.click();
			setTimeout(() => {
				document.body.removeChild(a);
			}, 100);
		} catch (error) {
			console.error("Error downloading image:", error);
			toast.error("Failed to download image");
		}
	};

	const handleReprompt = () => {
		if (onReprompt) {
			onReprompt(generation);
			onClose();
		} else {
			toast.info(t("assets.detail.repromptUnavailable"));
		}
	};

	const handleRegenerate = () => {
		if (onRegenerate) {
			onRegenerate(generation);
			onClose();
		} else {
			toast.info(t("assets.detail.regenerateUnavailable"));
		}
	};

	return (
		<DialogPrimitive.Root open={isOpen} onOpenChange={onClose}>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
				<DialogPrimitive.Content
					className={cn(
						"fixed inset-0 z-50 flex flex-col md:flex-row",
						// 移动端整页一条滚动轴，桌面端还是左图右栏各自不动
						"bg-card overflow-y-auto outline-none md:overflow-hidden",
						"data-[state=closed]:animate-out data-[state=open]:animate-in",
						"data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
					)}
					// 没有描述文字，明确告诉 Radix 别再找 aria-describedby
					aria-describedby={undefined}
					// 全屏时的 Esc 归 handleKeyDown 管，否则 Radix 会直接关掉整个弹窗
					onEscapeKeyDown={(event) => {
						if (isFullscreen) {
							event.preventDefault();
						}
					}}
				>
					{/* Radix 要求 Content 里必须有 Title，否则读屏用户拿不到弹窗名字。
					    这个弹窗的主体就是图片本身，没有可见的标题文字，所以藏起来 */}
					<DialogPrimitive.Title className="sr-only">
						{t("assets.detail.title")}
					</DialogPrimitive.Title>
					{/* Image display - top on mobile, left on desktop */}
					{/* min-h 保底，内容不够长时 flex-1 再把图区撑满剩余空间 */}
					<div className="relative flex-1 min-h-[52dvh] flex items-center justify-center bg-neutral-100 p-3 dark:bg-neutral-950 md:min-h-0 md:p-8">
						{/* 移动端靠右，桌面端右上被右侧详情面板占着所以靠左。用 flex 对齐而
						    不是 left/right 互相抵消——两边都有值时按钮会被拉成一整条 */}
						<div className="pointer-events-none absolute inset-x-3 top-3 z-10 flex justify-end md:inset-x-4 md:top-4 md:justify-start">
							<button
								type="button"
								onClick={onClose}
								className="pointer-events-auto rounded-full bg-black/70 p-2 text-white shadow-lg transition-all hover:bg-black dark:bg-white/90 dark:text-black dark:hover:bg-white"
								aria-label={t("assets.detail.close")}
							>
								<XIcon className="h-5 w-5" />
							</button>
						</div>

						{/* 按钮必须占满整块区域：高度是 auto 的话，图片的 max-h-full 这种
						    百分比约束会失效，图片就按原始尺寸溢出去 */}
						<button
							type="button"
							onClick={() => setIsFullscreen(true)}
							className="flex h-full w-full cursor-zoom-in items-center justify-center"
						>
							<WatermarkedImage
								src={currentImageUrl}
								alt={generation.originalPrompt}
								className="max-h-full max-w-full w-auto h-auto object-contain rounded-lg"
							/>
						</button>
					</div>

					{/* Details panel - bottom on mobile, right on desktop */}
					<div className="w-full shrink-0 flex flex-col bg-card border-t md:w-[320px] md:min-h-0 md:border-t-0 md:border-l">
						{/* Primary action bar — download is the most frequent action */}
						<div className="flex shrink-0 items-center justify-between gap-2 px-4 pt-4 md:px-6 md:pt-6">
							<Button
								variant="outline"
								className="h-10 gap-2 border-border text-foreground hover:bg-muted"
								onClick={() => handleDownload(currentImageUrl)}
							>
								<DownloadIcon className="h-4 w-4" />
								{t("assets.buttons.download")}
							</Button>
							<div className="flex items-center gap-1">
								{isImageFavorite && onToggleFavorite ? (
									<Button
										variant="ghost"
										size="icon"
										className={cn(
											"h-10 w-10 shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground",
											isImageFavorite(
												currentImageIndex,
											) && "text-yellow-500",
										)}
										aria-label={t(
											"assets.buttons.favorite",
										)}
										aria-pressed={isImageFavorite(
											currentImageIndex,
										)}
										onClick={() =>
											onToggleFavorite(currentImageIndex)
										}
									>
										<StarIcon
											className={cn(
												"h-4 w-4",
												isImageFavorite(
													currentImageIndex,
												) && "fill-current",
											)}
										/>
									</Button>
								) : null}
								{onDelete ? (
									<Button
										variant="ghost"
										size="icon"
										className="h-10 w-10 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
										aria-label={t(
											"assets.buttons.deleteImage",
										)}
										onClick={() =>
											onDelete(currentImageIndex)
										}
									>
										<Trash2Icon className="h-4 w-4" />
									</Button>
								) : null}
							</div>
						</div>

						{/* Kind badge + create time */}
						<div className="flex shrink-0 items-center justify-between gap-2 border-b px-4 py-3 md:px-6 md:py-4">
							<span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground">
								<ImageIcon className="h-4 w-4" />
								{t("assets.detail.badge")}
							</span>
							<span className="text-xs text-muted-foreground">
								{formatDateTime(new Date(generation.createdAt))}
							</span>
						</div>

						{/* Details content */}
						<div className="px-4 py-4 space-y-6 md:flex-1 md:overflow-y-auto md:px-6 md:py-6 md:space-y-8">
							{/* Reference Image */}
							{generation.inputImageUrls &&
							generation.inputImageUrls.length > 0 ? (
								<div className="space-y-2">
									<div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
										{t("assets.detail.referenceImage")}
									</div>
									<div className="flex gap-2 flex-wrap">
										{generation.inputImageUrls.map(
											(url, index) => (
												<img
													key={index}
													src={url}
													alt={`Reference ${index + 1}`}
													className="h-20 w-20 rounded-lg object-cover border bg-muted"
												/>
											),
										)}
									</div>
								</div>
							) : null}

							{/* Prompt */}
							<div className="space-y-2">
								<div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
									{t("assets.detail.prompt")}
								</div>
								<p
									className={cn(
										"text-sm text-foreground leading-relaxed bg-muted/30 p-3 rounded-lg border border-transparent hover:border-border transition-colors",
										isPromptLong &&
											!isPromptExpanded &&
											"line-clamp-6",
									)}
								>
									{generation.originalPrompt}
								</p>
								{isPromptLong ? (
									<button
										type="button"
										onClick={() =>
											setIsPromptExpanded(
												(expanded) => !expanded,
											)
										}
										className="text-xs font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground"
									>
										{isPromptExpanded
											? t("assets.detail.showLess")
											: t("assets.detail.showMore")}
									</button>
								) : null}
								{/* 规格拼成一行，缺的项直接不显示 */}
								<p className="text-xs text-muted-foreground">
									{[generation.model, aspectRatio, resolution]
										.filter(Boolean)
										.join("  |  ")}
								</p>
							</div>
						</div>

						{/* Action buttons */}
						<div className="shrink-0 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] md:p-6 md:pb-6">
							{onGenerateVideo ? (
								<>
									<div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
										{t("assets.detail.generate")}
									</div>
									<Button
										className="w-full h-11 gap-2 px-4 mb-4 border-foreground bg-foreground text-background shadow-none hover:bg-foreground/90 [&>svg]:opacity-100"
										onClick={() =>
											onGenerateVideo(currentImageUrl)
										}
									>
										<VideoIcon className="h-4 w-4" />
										{t("assets.detail.generateVideo")}
									</Button>
								</>
							) : null}
							<div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
								{t("assets.detail.more")}
							</div>
							<div className="flex items-center gap-2">
								<Button
									variant="outline"
									className="flex-1 h-11 gap-2 px-4 border-border text-foreground hover:bg-muted"
									onClick={handleReprompt}
								>
									<EditIcon className="h-4 w-4" />
									{t("assets.detail.reprompt")}
								</Button>
								<Button
									variant="outline"
									className="flex-1 h-11 gap-2 px-4 border-border text-foreground hover:bg-muted"
									onClick={handleRegenerate}
								>
									<RefreshCwIcon className="h-4 w-4" />
									{t("assets.detail.regenerate")}
								</Button>
							</div>
						</div>
					</div>

					{isFullscreen && (
						<div className="fixed inset-0 z-[60] flex items-center justify-center bg-black">
							{/* 点画面任何地方都退出；图片盖在它上面，所以放最底下 */}
							<button
								type="button"
								aria-label={t("assets.detail.close")}
								onClick={() => setIsFullscreen(false)}
								className="absolute inset-0 cursor-zoom-out"
							/>
							<WatermarkedImage
								src={currentImageUrl}
								alt={generation.originalPrompt}
								className="pointer-events-none relative max-h-full max-w-full object-contain"
							/>
							<button
								type="button"
								aria-label={t("assets.detail.close")}
								onClick={() => setIsFullscreen(false)}
								// 右上角：弹窗自己的关闭键在左上，两个 X 叠一起会分不清关的是哪层
								className="absolute top-4 right-4 rounded-full bg-black/70 p-2 text-white shadow-lg transition-colors hover:bg-black"
							>
								<XIcon className="h-5 w-5" />
							</button>
						</div>
					)}
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}
